import { MAX_STORAGE_SIZE, CHAT_STORAGE_KEY } from './config';

// Re-export for backward compatibility
export { CHAT_STORAGE_KEY };

// Helper function to safely parse JSON from localStorage
export const getStoredData = <T>(key: string, defaultValue: T): T => {
  try {
    const storedData = localStorage.getItem(key);
    if (!storedData) return defaultValue;
    
    return JSON.parse(storedData) as T;
  } catch (error) {
    console.error('Error retrieving data from localStorage:', error);
    return defaultValue;
  }
};

// Helper function to safely store JSON in localStorage
export const storeData = <T>(key: string, data: T): boolean => {
  try {
    const serializedData = JSON.stringify(data);
    
    // Check if we're exceeding the maximum storage size
    if (serializedData.length > MAX_STORAGE_SIZE) {
      console.warn('Data exceeds maximum storage size. Not saving to localStorage.');
      return false;
    }
    
    localStorage.setItem(key, serializedData);
    return true;
  } catch (error) {
    console.error('Error storing data in localStorage:', error);
    return false;
  }
};

// Helper function to clear a specific key from localStorage
export const clearStoredData = (key: string): boolean => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error clearing data from localStorage:', error);
    return false;
  }
};

// Check if localStorage is getting too large and clear if necessary
export const checkStorageSize = (): void => {
  try {
    let totalSize = 0;
    
    // Calculate total localStorage size
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key) || '';
        totalSize += key.length + value.length;
      }
    }
    
    // If approaching 80% of the max storage, clear the chat messages
    if (totalSize > MAX_STORAGE_SIZE * 0.8) {
      console.warn('localStorage approaching capacity. Clearing chat data.');
      clearStoredData(CHAT_STORAGE_KEY);
    }
  } catch (error) {
    console.error('Error checking localStorage size:', error);
  }
};
